import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import BannerCarousel from "../components/BannerCarousel";
import ProductCard from "../components/ProductCard";
import ServiceableAreaBanner from "../components/ServiceableAreaBanner";
import { useCatalog } from "../context/CatalogContext";
import { CategoryImage } from "../components/CategoryImage";
import { ArrowRight } from "lucide-react";

function Section({ title, tag, href, products }) {
  return (
    <section className="container-x py-8 md:py-12" data-testid={`section-${tag}`}>
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-terracotta font-semibold mb-1">{tag}</div>
          <h2 className="font-heading font-bold text-2xl md:text-3xl lg:text-4xl text-charcoal tracking-tight">{title}</h2>
        </div>
        <Link to={href} className="hidden md:inline-flex items-center gap-1 text-sm font-medium text-charcoal hover:text-terracotta">
          View all <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-6">
        {products.slice(0, 5).map((p) => (
          <ProductCard key={p.id} p={p} />
        ))}
      </div>
    </section>
  );
}

export default function HomePage() {
  const { categories, error: categoryError, refreshCategories } = useCatalog();
  const categoryName = (slug) => categories.find((c) => c.slug === slug)?.name;
  const [tiles, setTiles] = useState([]);
  const [stencils, setStencils] = useState([]);
  const [deals, setDeals] = useState([]);
  const [decor, setDecor] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/products", { params: { category: "tiles", sort: "rating", limit: 8 } }),
      api.get("/products", { params: { category: "wall-stencils", sort: "popular", limit: 8 } }),
      api.get("/products", { params: { section: "deals", sort: "rating", limit: 8 } }),
      api.get("/products", { params: { category: "home-decor", sort: "popular", limit: 8 } }),
    ]).then(([a, b, c, d]) => {
      setTiles(a.data); setStencils(b.data); setDeals(c.data); setDecor(d.data);
    });
  }, [categories]);

  return (
    <div>
      <ServiceableAreaBanner />
      <BannerCarousel />

      {/* Category quick shop */}
      {categoryError && <div className="container-x text-sm text-destructive py-3" data-testid="home-category-error">{categoryError}<button data-testid="home-category-retry" className="ml-2 underline" onClick={refreshCategories}>Retry</button></div>}
      <section className="container-x py-8" data-testid="quick-categories">
        <div className="grid grid-cols-3 md:grid-cols-7 gap-3 md:gap-4">
          {categories.map((c) => (
            <Link
              key={c.slug}
              to={`/category/${c.slug}`}
              data-testid={`quick-cat-${c.slug}`}
              className="group flex flex-col items-center text-center gap-2"
            >
              <div className="aspect-square w-full bg-white border border-border overflow-hidden group-hover:border-terracotta transition-colors">
                <CategoryImage category={c} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
              <span className="text-xs md:text-sm font-medium text-charcoal group-hover:text-terracotta">{c.name}</span>
            </Link>
          ))}
        </div>
      </section>

      {categoryName('tiles') && <Section title={`Best of ${categoryName('tiles')}`} tag="Editor's picks" href="/category/tiles" products={tiles} />}

      {/* Split promo band */}
      <section className="container-x py-4">
        <div className="grid md:grid-cols-2 gap-4">
          <div className="relative overflow-hidden aspect-[16/7] group">
            <img src="https://images.unsplash.com/photo-1615873968403-89e068629265?w=1200&q=80" alt="" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-charcoal/80 to-transparent" />
            {categoryName('wallpapers') && <div className="relative h-full flex flex-col justify-end p-6 md:p-8 text-off-white">
              <div className="text-xs uppercase tracking-[0.3em] text-terracotta-light font-semibold mb-2">{categoryName('wallpapers')}</div>
              <h3 className="font-heading font-bold text-2xl md:text-3xl mb-3">Statement walls, made simple</h3>
              <Link to="/category/wallpapers" className="btn-terracotta w-fit" data-testid="promo-wallpapers">Shop now</Link>
            </div>}
          </div>
          <div className="relative overflow-hidden aspect-[16/7] group">
            <img src="https://images.unsplash.com/photo-1724026502211-ff953e813194?w=1200&q=80" alt="" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-terracotta/80 to-transparent" />
            {categoryName('flooring') && <div className="relative h-full flex flex-col justify-end p-6 md:p-8 text-off-white">
              <div className="text-xs uppercase tracking-[0.3em] text-off-white font-semibold mb-2">{categoryName('flooring')}</div>
              <h3 className="font-heading font-bold text-2xl md:text-3xl mb-3">Underfoot warmth, all year</h3>
              <Link to="/category/flooring" className="btn-terracotta bg-off-white text-charcoal hover:bg-charcoal hover:text-off-white w-fit" data-testid="promo-flooring">Shop now</Link>
            </div>}
          </div>
        </div>
      </section>

      {categoryName('wall-stencils') && <Section title={`Trending ${categoryName('wall-stencils')}`} tag="What's hot" href="/category/wall-stencils" products={stencils} />}
      <Section title="Deals of the Day" tag="Up to 45% off" href="/category/all" products={deals} />
      {categoryName('home-decor') && <Section title={`Curated ${categoryName('home-decor')}`} tag="Handpicked" href="/category/home-decor" products={decor} />}
    </div>
  );
}
